import Immutable from 'immutable';
import invariant from 'invariant';

import { resetBusinessPlans } from './reset/resetBusinessPlans';
import { ScenarioConfiguration } from './Scenario';
import { BusinessPlanJSON, BusinessPlan } from './BusinessPlan';
import { EmailJSON, Email } from './Email';
import emailsInit from './reset/emailsInit';
import Submit from './submit';
import CheckEmails from './check';

export interface GameDataJSON {
  version: number;
  name: string;
  team: string;
  leader: boolean;
  createdAt: Date;
  lastSubmission: number;
  money: number;
  hearts: number;
  endingSubmission: number;
  active: boolean;
  viewMode: boolean;
  cashDecisions: string;
  cashDecisionsSaved: boolean;
  state: string;
  businessPlans: BusinessPlanJSON[];
  emails: EmailJSON[];
}

export class GameData {
  version: number = 0;
  name: string = '';
  team: string = '';
  leader: boolean = false;
  createdAt: Date = new Date();
  lastSubmission = 0;
  money = 0;
  hearts = 0;
  endingSubmission = 0;
  active = false;
  viewMode = false;
  cashDecisions = '';
  cashDecisionsSaved = false;
  state = '';
  businessPlans = Immutable.Map<number,BusinessPlan>();
  emails = Immutable.List<Email>();

  get currentBusinessPlan() {
    const businessPlan = this.businessPlans.get(this.lastSubmission);
    invariant(businessPlan, 
      `Cannot find current business plan for game [${this.name}] with submission [${this.lastSubmission}]!`);
    return businessPlan;
  }

  private sendEmails() {
    this.emails = CheckEmails(this)
      .reduce((acc,email) => acc.push(email), this.emails);
    return this;
  }

  setVariable(variableName: string, year: number, value: number) {
    invariant(this.state !== 'COMPLETED', 
      `Cannot set variable [${variableName}] on game [${this.name}] because the game is COMPLETED!`);
    
    this.businessPlans.set(
      this.currentBusinessPlan.submission,
      this.currentBusinessPlan.setVariable(variableName, year, value)
    );
    return this;
  }

  getVariableByNameAndYear(name: string, year: number) {
    return this.currentBusinessPlan.getVariable(name, year);
  }

  readEmail(emailID: string) {
    this.emails = this.emails.map(email => {
      if ( email.id === emailID ) {
        return email.read();
      } else {
        return email
      }
    })
    return this;
  }

  compute() {
    invariant(this.state !== 'COMPLETED',
      `Cannot compute game [${this.name}] because it is COMPLETED!`);
    
    this.businessPlans.set(
      this.currentBusinessPlan.submission,
      this.currentBusinessPlan.compute()
    );
    this.version += 1;
    return this;
  }

  reset(scenarioConfig: ScenarioConfiguration) {
    this.lastSubmission = 1;
    this.money = 8 * 7500;
    this.hearts = 8;
    this.endingSubmission = 3;
    this.active = true;
    this.viewMode = false;
    this.cashDecisions = '';
    this.cashDecisionsSaved = false;
    this.state = 'INIT';
    this.emails = Immutable.List(emailsInit.map(email => Email.fromJSON({ ...email, sentDate: new Date() })))
    this.businessPlans = resetBusinessPlans(scenarioConfig);
    return this;
  }

  submit() {
    return Submit(
      this
        .compute()
        .sendEmails()
    );
  }

  static fromJSON(json: GameDataJSON) {
    const gameData = new GameData();
    gameData.version = json.version;
    gameData.name = json.name;
    gameData.team = json.team;
    gameData.leader = json.leader;
    gameData.createdAt = json.createdAt;
    gameData.lastSubmission = json.lastSubmission;
    gameData.money = json.money;
    gameData.hearts = json.hearts;
    gameData.endingSubmission = json.endingSubmission;
    gameData.active = json.active;
    gameData.viewMode = json.viewMode;
    gameData.cashDecisions = json.cashDecisions;
    gameData.cashDecisionsSaved = json.cashDecisionsSaved;
    gameData.state = json.state;
    gameData.businessPlans = json.businessPlans && json.businessPlans.reduce((acc, json) => 
      acc.set(json.submission, BusinessPlan.fromJSON(json)),
      Immutable.Map<number, BusinessPlan>())
    gameData.emails = json.emails && Immutable.List(json.emails.map(json => Email.fromJSON(json)));

    return gameData;
  }

  toJSON(): GameDataJSON {
    return {
      version: this.version,
      name: this.name,
      team: this.team,
      leader: this.leader,
      createdAt: this.createdAt,
      lastSubmission: this.lastSubmission,
      money: this.money,
      hearts: this.hearts,
      endingSubmission: this.endingSubmission,
      active: this.active,
      viewMode: this.viewMode,
      cashDecisions: this.cashDecisions,
      cashDecisionsSaved: this.cashDecisionsSaved,
      state: this.state,
      businessPlans: this.businessPlans
        .toList()
        .toArray()
        .map(businessPlan => businessPlan.toJSON()),
      emails: this.emails
        .toArray()
        .map(email => email.toJSON()),
    }
  }
}